import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, Info } from "@phosphor-icons/react";
import SEO, { faqSchema, calculatorSchema, breadcrumbSchema } from "@/components/SEO";
import RelatedCalculators from "@/components/RelatedCalculators";
import { getCalculatorBySlug } from "@/data/calculatorsData";

const faqItems = [
  { q: "How is crypto taxed in India?", a: "All gains from virtual digital assets (VDAs) including Bitcoin, Ethereum, and other cryptocurrencies are taxed at a flat 30% under Section 115BBH of the Income Tax Act. Additionally, a 4% health and education cess is applied on the tax amount, and surcharge applies based on your total income." },
  { q: "Can I offset crypto losses against other income?", a: "No. Section 115BBH explicitly prohibits setting off losses from crypto against any other source of income, including salary, business income, or capital gains from stocks or real estate. You also cannot carry forward crypto losses to future years." },
  { q: "Is the 30% crypto tax applicable on every trade?", a: "The 30% tax applies on the profit (gain) from each transfer of a VDA. A transfer includes selling crypto for INR, swapping one crypto for another, using crypto to buy goods or services, or gifting crypto above Rs 50,000." },
  { q: "Do I need to pay advance tax on crypto gains?", a: "Yes. If your total crypto tax liability for the year exceeds Rs 10,000, you are required to pay advance tax in quarterly installments. Missing advance tax deadlines attracts interest under Sections 234B and 234C." },
  { q: "What expenses can I deduct from crypto gains?", a: "Under Section 115BBH, the only deduction allowed is the cost of acquisition. You cannot deduct trading fees, gas fees, platform charges, internet costs, or any other expense from your crypto gains." }
];

export default function CryptoTaxCalculator() {
  const [buyPrice, setBuyPrice] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [income, setIncome] = useState("below50");
  const [result, setResult] = useState(null);
  const calcData = getCalculatorBySlug("crypto-tax-calculator-india");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const calculate = () => {
    const buy = parseFloat(buyPrice) || 0;
    const sell = parseFloat(sellPrice) || 0;
    const qty = parseFloat(quantity) || 0;
    const totalBuy = buy * qty;
    const totalSell = sell * qty;
    const gain = totalSell - totalBuy;

    if (gain <= 0) {
      setResult({ gain, tax: 0, surcharge: 0, cess: 0, total: 0, effective: 0, loss: true });
      return;
    }

    const baseTax = gain * 0.30;
    let surchargeRate = 0;
    if (income === "50to1cr") surchargeRate = 0.10;
    else if (income === "1crto2cr") surchargeRate = 0.15;
    else if (income === "2crto5cr") surchargeRate = 0.25;
    else if (income === "above5cr") surchargeRate = 0.37;

    const surcharge = baseTax * surchargeRate;
    const taxPlusSurcharge = baseTax + surcharge;
    const cess = taxPlusSurcharge * 0.04;
    const total = taxPlusSurcharge + cess;
    const effective = gain > 0 ? (total / gain) * 100 : 0;

    setResult({ gain, tax: baseTax, surcharge, cess, total, effective, loss: false, totalBuy, totalSell });
  };

  const fmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);

  return (
    <div data-testid="crypto-tax-calculator-page">
      <SEO
        title="Crypto Tax Calculator India | Section 115BBH Tax Calculator 2026"
        description="Free crypto tax calculator for India. Calculate your 30% tax liability on crypto gains under Section 115BBH. Includes surcharge, health and education cess, and advance tax estimation."
        path="/calculators/crypto-tax-calculator-india"
        keywords={calcData.tags}
        jsonLd={[
          faqSchema(faqItems),
          calculatorSchema(calcData),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Free Tools", path: "/tools" },
            { name: "Crypto Tax Calculator", path: "/calculators/crypto-tax-calculator-india" }
          ])
        ]}
      />

      <section className="bg-[#21324D] py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-sm uppercase tracking-[0.2em] text-[#A7A55C] mb-6">Free Tool</p>
            <h1 className="font-heading font-semibold text-4xl sm:text-5xl text-[#F5F5F2] leading-none tracking-tight mb-6" data-testid="calc-heading">
              Crypto Tax Calculator India
            </h1>
            <p className="font-body text-lg text-[#F5F5F2]/75 leading-relaxed">
              Calculate your crypto tax liability under Section 115BBH. India taxes all crypto gains at a flat 30% plus applicable surcharge and 4% cess.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="bg-white border border-[#383A46]/10 p-8 sm:p-10" data-testid="calc-form">
                <h2 className="font-body font-medium text-lg text-[#21324D] mb-6">Enter your trade details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block font-body text-sm font-medium text-[#21324D] mb-2">Buy Price per Unit (INR)</label>
                    <input type="number" value={buyPrice} onChange={(e) => setBuyPrice(e.target.value)} placeholder="e.g. 2500000" className="w-full bg-[#F5F5F2] border border-[#383A46]/15 px-4 py-3 font-body text-sm text-[#21324D] focus:outline-none focus:border-[#A7A55C]" data-testid="calc-buy-price" />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-[#21324D] mb-2">Sell Price per Unit (INR)</label>
                    <input type="number" value={sellPrice} onChange={(e) => setSellPrice(e.target.value)} placeholder="e.g. 3500000" className="w-full bg-[#F5F5F2] border border-[#383A46]/15 px-4 py-3 font-body text-sm text-[#21324D] focus:outline-none focus:border-[#A7A55C]" data-testid="calc-sell-price" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                  <div>
                    <label className="block font-body text-sm font-medium text-[#21324D] mb-2">Quantity</label>
                    <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="e.g. 0.5" step="any" className="w-full bg-[#F5F5F2] border border-[#383A46]/15 px-4 py-3 font-body text-sm text-[#21324D] focus:outline-none focus:border-[#A7A55C]" data-testid="calc-quantity" />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-[#21324D] mb-2">Total Income Slab</label>
                    <select value={income} onChange={(e) => setIncome(e.target.value)} className="w-full bg-[#F5F5F2] border border-[#383A46]/15 px-4 py-3 font-body text-sm text-[#21324D] focus:outline-none focus:border-[#A7A55C]" data-testid="calc-income-slab">
                      <option value="below50">Below Rs 50 Lakh</option>
                      <option value="50to1cr">Rs 50 Lakh - 1 Cr</option>
                      <option value="1crto2cr">Rs 1 Cr - 2 Cr</option>
                      <option value="2crto5cr">Rs 2 Cr - 5 Cr</option>
                      <option value="above5cr">Above Rs 5 Cr</option>
                    </select>
                  </div>
                </div>
                <button onClick={calculate} className="w-full bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors font-body font-semibold text-sm py-4 flex items-center justify-center gap-2" data-testid="calc-calculate-btn">
                  <Calculator size={18} weight="bold" /> Calculate Tax
                </button>
              </div>

              {result && (
                <div className="bg-white border border-[#A7A55C]/30 p-8 sm:p-10 mt-6" data-testid="calc-result">
                  {result.loss ? (
                    <div>
                      <h3 className="font-body font-medium text-lg text-[#21324D] mb-3">No Tax Applicable</h3>
                      <p className="font-body text-sm text-[#383A46]">Your trade resulted in a loss of <strong>Rs {fmt(Math.abs(result.gain))}</strong>. No tax is payable on crypto losses under Section 115BBH. Note: this loss cannot be offset against other income or carried forward.</p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="font-body font-medium text-lg text-[#21324D] mb-6">Tax Breakdown</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-[#383A46]/10"><span className="font-body text-sm text-[#383A46]">Total Buy Value</span><span className="font-body text-sm font-medium text-[#21324D]">Rs {fmt(result.totalBuy)}</span></div>
                        <div className="flex justify-between py-2 border-b border-[#383A46]/10"><span className="font-body text-sm text-[#383A46]">Total Sell Value</span><span className="font-body text-sm font-medium text-[#21324D]">Rs {fmt(result.totalSell)}</span></div>
                        <div className="flex justify-between py-2 border-b border-[#383A46]/10"><span className="font-body text-sm text-[#A7A55C] font-medium">Capital Gain</span><span className="font-body text-sm font-semibold text-[#A7A55C]">Rs {fmt(result.gain)}</span></div>
                        <div className="flex justify-between py-2 border-b border-[#383A46]/10"><span className="font-body text-sm text-[#383A46]">Tax @ 30% (Section 115BBH)</span><span className="font-body text-sm font-medium text-[#21324D]">Rs {fmt(result.tax)}</span></div>
                        {result.surcharge > 0 && <div className="flex justify-between py-2 border-b border-[#383A46]/10"><span className="font-body text-sm text-[#383A46]">Surcharge</span><span className="font-body text-sm font-medium text-[#21324D]">Rs {fmt(result.surcharge)}</span></div>}
                        <div className="flex justify-between py-2 border-b border-[#383A46]/10"><span className="font-body text-sm text-[#383A46]">Health & Education Cess (4%)</span><span className="font-body text-sm font-medium text-[#21324D]">Rs {fmt(result.cess)}</span></div>
                        <div className="flex justify-between py-3 bg-[#21324D] px-4 -mx-4 mt-4"><span className="font-body text-sm font-semibold text-[#F5F5F2]">Total Tax Payable</span><span className="font-body text-base font-semibold text-[#A7A55C]">Rs {fmt(result.total)}</span></div>
                        <div className="flex justify-between py-2"><span className="font-body text-xs text-[#383A46]/60">Effective Tax Rate</span><span className="font-body text-xs font-medium text-[#383A46]/60">{result.effective.toFixed(2)}%</span></div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="lg:col-span-5">
              <div className="bg-[#152F2C] p-8 mb-6">
                <Info size={20} weight="duotone" className="text-[#A7A55C] mb-4" />
                <h3 className="font-body font-medium text-base text-[#F5F5F2] mb-3">How crypto tax works in India</h3>
                <ul className="space-y-3">
                  <li className="font-body text-sm text-[#F5F5F2]/65 leading-relaxed">Flat 30% tax on all crypto gains under Section 115BBH</li>
                  <li className="font-body text-sm text-[#F5F5F2]/65 leading-relaxed">No distinction between short-term and long-term gains</li>
                  <li className="font-body text-sm text-[#F5F5F2]/65 leading-relaxed">Only cost of acquisition is deductible</li>
                  <li className="font-body text-sm text-[#F5F5F2]/65 leading-relaxed">Losses cannot be offset against any income</li>
                  <li className="font-body text-sm text-[#F5F5F2]/65 leading-relaxed">1% TDS on transactions under Section 194S</li>
                </ul>
              </div>
              <div className="border border-[#383A46]/10 p-8 bg-white">
                <h3 className="font-body font-medium text-base text-[#21324D] mb-3">Need help with crypto taxes?</h3>
                <p className="font-body text-sm text-[#383A46]/70 leading-relaxed mb-5">This calculator provides estimates. For comprehensive crypto tax planning, optimization, and ITR filing support, consult with BitGenix.</p>
                <Link to="/contact" className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors font-body font-semibold text-sm px-6 py-3 inline-flex items-center gap-2" data-testid="calc-cta">
                  Get Expert Help <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white" data-testid="calc-faq">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-[#21324D] tracking-tight mb-10">Frequently Asked Questions</h2>
          <div className="space-y-0">
            {faqItems.map((item, i) => (
              <FAQItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#21324D]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-[#F5F5F2] mb-4">Want professional crypto tax planning?</h2>
          <p className="font-body text-sm text-[#F5F5F2]/65 max-w-lg mx-auto mb-8">BitGenix has helped 140+ clients navigate India's crypto tax regime. Book a free consultation to discuss your specific situation.</p>
          <Link to="/contact" className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors font-body font-semibold text-sm px-8 py-4 inline-flex items-center gap-2" data-testid="calc-bottom-cta">
            Book a Free Consultation <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <RelatedCalculators currentSlug="crypto-tax-calculator-india" />
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#383A46]/10">
      <button onClick={() => setOpen(!open)} className="w-full text-left py-5 flex items-start justify-between gap-4">
        <span className="font-body font-medium text-base text-[#21324D]">{q}</span>
        <span className="text-[#A7A55C] text-sm mt-1 flex-shrink-0">{open ? "−" : "+"}</span>
      </button>
      {open && <p className="font-body text-sm text-[#383A46] leading-relaxed pb-5 pr-8">{a}</p>}
    </div>
  );
}
